<template>
  <div :class="`ckeditor-wrapper ${theme}-theme`" v-no-capture>
    <textarea class="d-none" name="" :id="id"></textarea>
  </div>
</template>

<script>
import { KEY_CODES } from '@/constants/keyCodes'
import { MIME_TYPE, xsrfCookieName, xsrfHeaderName } from '@/constants/common'
import getCookie from '@/utils/getCookie'
import { isCtrlEvent } from '@/utils/kbEvents'
import throttle from '@/utils/throttle'

let ckeFetchStatus = null
const editorHeadingHeight = 41

/**
 * This is a wrapper component for CKEditor
 */
export default {
  name: 'CkEditor',
  props: {
    /**
     CKEditor config
     */
    config: Object,
    /**
     Used as an identifier for CKEditor instance
     */
    id: String,
    /**
     initial data to load CKEditor with
     */
    initialData: String,
    /**
     this is the type of initialData
     possible values - text/plain, text/html
     */
    initialDataType: { type: String, default: MIME_TYPE.HTML },
    /**
     Possible values:
     - dark : It gives grey bg to editor
     - light : It gives white bg to editor
     */
    theme: { default: 'light' },
    /**
     * Set this flag if you want to set have the default height of editor to be equivalent to
     * the height of content it is loaded with
     */
    maxHeightOnLaunch: Boolean
  },
  data() {
    return {
      loadTriggered: false,
      pendingTasks: []
    }
  },
  computed: {
    user() {
      return this.$store.getters.user
    },
    mentionableUsers() {
      return this.config.users || []
    }
  },
  methods: {
    focus() {
      this.ckeditor.focus()
    },
    setData(data, dataType = MIME_TYPE.HTML) {
      const setter = () => {
        if (dataType === MIME_TYPE.TEXT) {
          data = this.formatPlainTextToHTML(data)
        }
        this.ckeditor.setData(data)
      }
      if (this.ckeditor) {
        setter()
      } else {
        // delay until ckeditor is loaded
        this.pendingTasks.push(setter)
      }
    },
    formatPlainTextToHTML(data) {
      return data.replace(/\n/g, '<br/>').replace(/\s/g, '&nbsp;')
    },
    getData() {
      return this.ckeditor.getData()
    },
    /**
     * Customize the Image dialog
     * Below changes have been made
     * - Hide Tabs
     * - While adding new image, show content of Upload tab only, hide footer
     * - Trigger Submit on Click of Upload
     * - While editing, show content of Info only
     * - Custom styling
     */
    configureImageDialog(ev) {
      const dialogDefinition = ev.data.definition
      ev.data.definition.dialog.on('show', function() {
        const { src } = this.getModel().data
        const dialogEl = this._.element.$
        dialogEl.querySelector('.cke_dialog_ui_fileButton span').innerText = 'Upload'
        dialogEl.querySelector('.cke_dialog_ui_file label').classList.add('hide')
        dialogEl.querySelector('.cke_dialog_body').classList.add('hide-tabs')
        if (!src) {
          this.selectPage('Upload')
          dialogEl.querySelector('.cke_dialog_footer').classList.add('hide')
        } else {
          dialogEl.querySelector('.cke_dialog_footer').classList.remove('hide')
        }
      })
      const uploadTab = dialogDefinition.getContents('Upload')
      const uploadButton = uploadTab.get('uploadButton')
      uploadButton.filebrowser.onSelect = function(fileUrl) {
        const children = CKEDITOR.dialog.getCurrent().getContentElement('info')._.children
        const src = children.find(x => x.id === 'src')
        if (src) {
          src.setValue(fileUrl)
          ev.data.dialog.getButton('ok').onClick(ev)
        }
      }
    },
    emitSaveEvent: throttle(function() {
      /**
       * @emits
       * This is emitted when user types cmd + return in the editor
       */
      this.$emit('save', this.ckeditor.getData())
    }, 1000),
    renderEditor() {
      CKEDITOR.replace(this.id, this.config)
      this.ckeditor = CKEDITOR.instances[this.id]
      const configuredHeight = this.config.height
      this.ckeditor.on('instanceReady', () => {
        this.initialData && this.setData(this.initialData, this.initialDataType)
        if (this.maxHeightOnLaunch) {
          const { height } = this.ckeditor.document.$.body.getBoundingClientRect()
          this.ckeditor.resize(
            null,
            Math.max(configuredHeight, height + editorHeadingHeight + 10) // adding 10 as offset to prevent scroll
          )
        }
        this.pendingTasks.forEach(task => task())
        this.pendingTasks = []
        /**
         * @emits
         * This is emitted when editor is loaded
         */
        this.$emit('loaded')
      })
      this.ckeditor.on('key', ev => {
        const event = ev.data.domEvent.$
        if (isCtrlEvent(event) && event.keyCode === KEY_CODES.ENTER) {
          this.emitSaveEvent()
        }
        /**
         * @emits
         * This is emitted on key up
         */
        this.$emit('key', event)
      })
      this.ckeditor.on('change', () => {
        /**
         * @emits
         * This is emitted on content change
         */
        this.$emit('change', this.ckeditor.getData())
      })
      this.ckeditor.on('fileUploadRequest', function(evt) {
        const xhr = evt.data.fileLoader.xhr
        xhr.setRequestHeader(xsrfHeaderName, getCookie(xsrfCookieName))
      })
    },
    loadEditor() {
      const self = this
      if (!ckeFetchStatus) {
        const script = document.createElement('script')
        script.setAttribute('src', '/ckeditor/4.16.0/ckeditor.js')
        document.body.appendChild(script)
        ckeFetchStatus = 'FETCHING'
        script.onload = () => {
          ckeFetchStatus = 'FETCHED'
          this.renderEditor()
          CKEDITOR.addCss(`a[data-user-id='${self.user.id}'] { color: #3AB686 !important;}`)
          CKEDITOR.timestamp = 1611262605456 // new Date().getTime()
        }
        script.onerror = () => {
          ckeFetchStatus = null
        }
      } else if (ckeFetchStatus === 'FETCHED') {
        this.renderEditor()
      } else if (ckeFetchStatus === 'FETCHING') {
        const intervalId = setInterval(() => {
          if (ckeFetchStatus === 'FETCHED') {
            clearInterval(intervalId)
            this.renderEditor()
          }
        }, 500)
      }
    },
    scrollToBottom() {
      const element = this.ckeditor.document.$.body?.parentElement
      element.scrollTop = element.scrollHeight
    },
    /**
     * This is used by callee when cursor is required to be placed at the bottom
     */
    moveCursorToTheEnd() {
      const task = () => {
        this.ckeditor.focus()
        const range = this.ckeditor.createRange()
        range.moveToElementEditablePosition(range.root, true)
        this.ckeditor.getSelection().selectRanges([range])
        this.scrollToBottom()
      }
      if (this.ckeditor) {
        setTimeout(task, 0)
      } else {
        // delay until ckeditor is loaded
        this.pendingTasks.push(task)
      }
    }
  },
  mounted() {
    if (!this.config.mentions || this.mentionableUsers.length > 0) {
      this.loadTriggered = true
      this.loadEditor()
    }
  },
  beforeDestroy() {
    CKEDITOR.instances[this.id].destroy()
  },
  watch: {
    mentionableUsers(newValue, oldValue) {
      if (!this.loadTriggered && newValue.length > 0 && oldValue.length === 0) {
        this.loadEditor()
      }
    }
  }
}
</script>
<style lang="scss">
@import '~@/styles/ckeditor';
</style>
<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.ckeditor-wrapper {
  &.dark-theme {
    /deep/ a.cke_button_on,
    /deep/ a.cke_button_off:hover,
    /deep/ a.cke_button_off:focus,
    /deep/ a.cke_button_off:active,
    /deep/ .cke_toolbox a.cke_combo_button:hover,
    /deep/ .cke_toolbox a.cke_combo_button:active {
      background: white !important;
    }

    /deep/ .cke_wysiwyg_frame,
    /deep/ .cke_top,
    /deep/ a.cke_combo_button,
    /deep/ .cke_contents,
    /deep/ .cke_inner {
      background: $quinary !important;
    }
  }

  .cke {
    width: 100%;
  }
}
</style>

<style lang="scss">
.ckeditor-wrapper {
  container-type: inline-size;
}

@container (width < 650px) {
  .cke_inner {
    .cke_button__image,
    .cke_button__table,
    .cke_button__emojipanel {
      display: none !important;
    }
  }
}

@container (width < 540px) {
  .cke_inner {
    .cke_combo__format {
      display: none !important;
    }
  }
}

@container (width < 450px) {
  .cke_toolbox > span:not(:first-child) {
    display: none !important;
  }
}
</style>
